import { Content, Header, Link, Page } from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';
import { StatusCard } from '../status/StatusCard';
import { ServerList, ToolNames, rowOf, type ServerRow } from './ServerList';
import { CliCard, ConnectCard, JamCard } from './SideCards';
import {
  OTHER_SERVERS,
  PLANNED_PORTAL_TOOLS,
  PORTAL_SETUP,
  issueUrl,
} from './servers';
import { useMcpTools, type McpTool } from './useMcpTools';
import type { Loaded } from '../catalog/pipeline/useInsights';

const useStyles = makeStyles(theme => ({
  columns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 22rem)',
    gap: theme.spacing(2.5),
    alignItems: 'start',
    [theme.breakpoints.down('md')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    minWidth: 0,
  },
  servers: { overflowX: 'auto' },
  planned: { marginTop: theme.spacing(0.5), fontSize: '0.8125rem' },
  muted: { color: 'var(--ui-text-muted)' },
  note: { color: 'var(--ui-text-muted)', fontSize: '0.8125rem' },
  code: { fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' },
}));

/**
 * What the portal's own server offers, from its endpoint's answer: the tools
 * it lists, then the ones still planned, each naming the issue that builds it.
 * A planned tool the endpoint already lists is shown with the others.
 */
const PortalTools = ({ tools }: { tools: Loaded<McpTool[]> }) => {
  const classes = useStyles();
  const offered = new Set((tools.data ?? []).map(t => t.name));
  const planned = PLANNED_PORTAL_TOOLS.filter(t => !offered.has(t.name));
  return (
    <>
      {tools.loading && (
        <span className={classes.muted}>Asking the endpoint…</span>
      )}
      {tools.error && (
        <span className={classes.muted}>
          The endpoint could not say which tools it offers, so none is shown as
          live.
        </span>
      )}
      {tools.data && (
        // A named group, so a screen reader says what the names are: the
        // live ones, as against the planned ones beneath.
        <div role="group" aria-label="Tools the endpoint offers">
          <ToolNames names={tools.data.map(t => t.name)} />
        </div>
      )}
      {planned.map(t => (
        <div key={t.name} className={classes.planned}>
          <ToolNames names={[t.name]} /> —{' '}
          <Link to={issueUrl(t.issue)}>Planned · {t.issue}</Link>
        </div>
      ))}
    </>
  );
};

/**
 * MCP & CLI (DX-501, specs/mcp-and-cli.md): every MCP server a colleague can
 * connect Claude to, and the tools that run on their own machine.
 *
 * The portal's own server is the one row the page does not take on trust: its
 * tools and its state come from asking the endpoint, as the person reading.
 * The other servers sit behind sign-ins of their own and are written down in
 * `servers.ts`.
 */
export const McpPage = () => {
  const classes = useStyles();
  const tools = useMcpTools();

  let state: ServerRow['state'] = { kind: 'live' };
  if (tools.loading) state = { kind: 'asking' };
  else if (tools.error) {
    state = {
      kind: 'unknown',
      reason: 'The endpoint gave no tool list when the page asked.',
    };
  }

  const rows: ServerRow[] = [
    {
      id: 'backstage',
      name: 'Backstage',
      address: tools.address,
      offers: <PortalTools tools={tools} />,
      signIn: 'ZITADEL',
      state,
      setup: { label: 'MCP integrations', href: PORTAL_SETUP },
    },
    ...OTHER_SERVERS.map(rowOf),
  ];

  return (
    <Page themeId="home">
      <Header
        title="MCP & CLI"
        subtitle="Connect Claude, your IDE and local environment to the platform."
      />
      <Content>
        <div className={classes.columns}>
          <div className={classes.stack}>
            <StatusCard
              id="mcp-servers"
              title="MCP servers"
              aside="One connector per domain, with Backstage as the front door"
            >
              <div className={classes.servers}>
                <ServerList rows={rows} />
              </div>
            </StatusCard>
            <Typography className={classes.note}>
              Atlas's MCP server is being replaced by this portal's. Read
              Atlas's pages through Backstage's{' '}
              <span className={classes.code}>docs_search</span> and{' '}
              <span className={classes.code}>docs_get_page</span>.
            </Typography>
          </div>
          <div className={classes.stack}>
            <ConnectCard address={tools.address} />
            <JamCard />
            <CliCard />
          </div>
        </div>
      </Content>
    </Page>
  );
};
